import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  PropertyLocationAddressForm,
  PropertyUpdateDataApi,
} from '../models/propertyModel';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import {
  getPropertyToEditEndpoint,
  updatePropertyEndpoint,
} from '../services/propertyService';
import { useToast } from '../../../components/toast/ToastProvider';
import { getAddressDetailById } from '../../../services/addressService';

interface Props {
  id: string;
}
const useAdminAddressLocation = ({ id }: Props) => {
  const toast = useToast();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [formUpdateData, setFormUpdateData] = useState<PropertyUpdateDataApi>();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
  } = useForm<PropertyLocationAddressForm>({
    resolver: yupResolver(
      yup
        .object({
          address_id: yup
            .string()
            .nullable()
            .required('Ubicación/polgígono es requerido'),
          lat: yup.number().required('Latitud es requerida'),
          lng: yup.number().required('Longitud es requerida'),
          address: yup.string().required('Dirección escrita es requerida'),
        })
        .required(),
    ),
  });

  const [currentAddress, setCurrentAddress] = useState<any>();

  const loadExistingProperty = useCallback(async () => {
    const { status, data } = await callEndpoint(
      getPropertyToEditEndpoint(id, token),
    );
    if (status === 200) {
      const { data: formData } = fetchResponseAdapter(data);
      setFormUpdateData(formData);
    }
  }, [id, token, callEndpoint]);

  useEffect(() => {
    loadExistingProperty().catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (formUpdateData) {
      reset({
        lat: formUpdateData.lat,
        lng: formUpdateData.lng,
        address_id: formUpdateData.location?.id.toString() ?? '',
        address: formUpdateData.address ?? '',
      });
    }
  }, [formUpdateData]);

  const doAction = async (form: PropertyLocationAddressForm) => {
    const { status, data } = await callEndpoint(
      updatePropertyEndpoint(form, id, token),
    );
    if (status === 200) {
      // Se queda en esta pantalla: aviso y recarga de los datos guardados (la sección
      // de lugares cercanos usa las coordenadas de la propiedad).
      toast.success('Ubicación actualizada.');
      await loadExistingProperty();
    } else {
      toast.error(data?.message ?? 'No se pudo actualizar la ubicación.');
    }
  };

  return {
    isLoading,
    control,
    register,
    setValue,
    errors,
    handleSubmit,
    watch,
    formUpdateData,
    doAction,
    currentAddress,
  };
};

export default useAdminAddressLocation;
